import { useMemo, useState } from 'react';
import { useLoadoutStore } from '../../store/loadoutSlice';
import {
  TRINKET_MAJOR_REGISTRY,
  TRINKET_MINOR_REGISTRY,
} from '../../data/trinkets.config';
import {
  canAfford,
  computeTrinketLevelUpCost,
  levelUpTrinket,
} from '../../services/CommodeService';
import {
  describeTrinketCurrent,
  describeTrinketUpgrade,
} from '../../services/TrinketDescriptionService';
import {
  trinketCard,
  resolveCardClosure,
} from '../../services/CardDescriptionService';
import { closeInterface } from '../../services/InterfaceService';
import { ITEMS } from '../../data/inventory.config';
import { ItemList, ItemListEntry } from './ItemList';
import { ItemCard } from './ItemCard';
import { KeyDisplay } from './KeyDisplay';
import { DescLines } from './DescLines';
import { NineSliceFrame } from './NineSliceFrame';
import { UI_FORGE_GRID_FRAME_REGION } from '../../data/ui.atlas';
import type { CraftCost } from '../../types/craft.types';
import type {
  TrinketMajorDefinition,
  TrinketMinorDefinition,
} from '../../types/trinket.types';
import './CommodeScreen.scss';

type ItemKind = 'major' | 'minor';

interface ItemEntry {
  navId: string;
  kind: ItemKind;
  modelId: string;
  name: string;
  icon?: string;
  /** Trinket level (majors/minors). */
  level: number;
}

const KIND_LABEL: Record<ItemKind, string> = {
  major: 'Trésor',
  minor: 'Babiole',
};

const TABS: { kind: ItemKind; label: string }[] = [
  { kind: 'major', label: 'Trésors' },
  { kind: 'minor', label: 'Babioles' },
];

const GRID_COLS = 5;
const GRID_ROWS = 4;

function resolveDef(
  entry: ItemEntry,
): TrinketMajorDefinition | TrinketMinorDefinition | undefined {
  if (entry.kind === 'major') {
    return TRINKET_MAJOR_REGISTRY[entry.modelId];
  }
  return TRINKET_MINOR_REGISTRY[entry.modelId];
}

export const CommodeScreen = () => {
  const majorTrinkets = useLoadoutStore((s) => s.majorTrinkets);
  const minorTrinkets = useLoadoutStore((s) => s.minorTrinkets);

  // The entry currently under upgrade confirmation (null = browsing).
  const [confirming, setConfirming] = useState<ItemEntry | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [focusedNavId, setFocusedNavId] = useState<string | null>(null);

  const items = useMemo<ItemEntry[]>(() => {
    const list: ItemEntry[] = [];
    for (const [modelId, level] of Object.entries(majorTrinkets)) {
      const def = TRINKET_MAJOR_REGISTRY[modelId];
      if (def) {
        list.push({
          navId: `item:${modelId}`,
          kind: 'major',
          modelId,
          name: def.name,
          icon: def.icon,
          level,
        });
      }
    }
    for (const [modelId, level] of Object.entries(minorTrinkets)) {
      const def = TRINKET_MINOR_REGISTRY[modelId];
      if (def) {
        list.push({
          navId: `item:${modelId}`,
          kind: 'minor',
          modelId,
          name: def.name,
          icon: def.icon,
          level,
        });
      }
    }
    return list;
  }, [majorTrinkets, minorTrinkets]);

  const tabItems = useMemo(
    () => items.filter((it) => it.kind === TABS[activeTab].kind),
    [items, activeTab],
  );

  const focused = tabItems.find((it) => it.navId === focusedNavId) ?? null;

  // No floating card: the left DetailPanel shows the focused trinket instead.
  const listItems: ItemListEntry[] = tabItems.map((e) => ({
    id: e.navId,
    navId: e.navId,
    icon: e.icon,
    card: null,
  }));

  const onListConfirm = (): boolean => {
    if (confirming) {
      levelUpTrinket(confirming.modelId);
      setConfirming(null);
      return true;
    }
    if (focused && canUpgrade(focused)) {
      setConfirming(focused);
      return true;
    }
    // Nothing focused, at cap, or not enough resources → denied.
    return false;
  };

  const onListCancel = () => {
    if (confirming) {
      setConfirming(null);
    } else {
      closeInterface();
    }
  };

  const switchTab = (delta: number) => {
    if (confirming) {
      return;
    }
    setActiveTab((i) => (i + delta + TABS.length) % TABS.length);
  };

  const confirmDef =
    confirming &&
    (confirming.kind === 'major'
      ? TRINKET_MAJOR_REGISTRY[confirming.modelId]
      : TRINKET_MINOR_REGISTRY[confirming.modelId]);
  const confirmCost =
    confirming && confirmDef
      ? computeTrinketLevelUpCost(confirmDef, confirming.level)
      : null;

  return (
    <div className="commode-overlay">
      <div className="commode-frame">
        <div className="commode-content">
          <NineSliceFrame className="commode-detail-frame">
            <DetailPanel entry={focused} />
          </NineSliceFrame>
          <div className="commode-right">
            <div className="commode-tabs">
              {TABS.map((t, i) => (
                <div
                  key={t.kind}
                  className={`commode-tab${i === activeTab ? ' active' : ''}`}
                >
                  {t.label}
                </div>
              ))}
            </div>
            <NineSliceFrame
              region={UI_FORGE_GRID_FRAME_REGION}
              className="commode-grid-frame"
            >
              <ItemList
                active
                frozen={confirming !== null}
                items={listItems}
                cols={GRID_COLS}
                maxRows={GRID_ROWS}
                onConfirm={onListConfirm}
                onCancel={onListCancel}
                onTabNext={() => switchTab(1)}
                onTabPrev={() => switchTab(-1)}
                onFocusChange={setFocusedNavId}
              />
              {tabItems.length === 0 && (
                <div className="commode-grid-empty">
                  Aucun objet dans cet onglet.
                </div>
              )}
            </NineSliceFrame>
          </div>
        </div>
      </div>

      <div className="commode-hint">
        <KeyDisplay context="interface" action="CONFIRM" size="sm" /> améliorer
        &nbsp;·&nbsp;
        <KeyDisplay context="interface" action="CANCEL" size="sm" /> retour
      </div>

      {confirming && (
        <div className="commode-confirm">
          <div className="confirm-box">
            <div className="confirm-msg">
              Améliorer <strong>{confirming.name}</strong> au niveau{' '}
              {confirming.level + 1} ?
            </div>
            {confirmCost && <CostDisplay cost={confirmCost} />}
            <div className="confirm-actions">
              <span>
                <KeyDisplay context="interface" action="CONFIRM" size="sm" />{' '}
                Valider
              </span>
              <span>
                <KeyDisplay context="interface" action="CANCEL" size="sm" />{' '}
                Annuler
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function canUpgrade(entry: ItemEntry): boolean {
  const def =
    entry.kind === 'major'
      ? TRINKET_MAJOR_REGISTRY[entry.modelId]
      : TRINKET_MINOR_REGISTRY[entry.modelId];
  if (!def || entry.level >= def.maxLevel) {
    return false;
  }
  const cost = computeTrinketLevelUpCost(def, entry.level);
  return !!cost && canAfford(cost);
}

const DetailPanel = ({ entry }: { entry: ItemEntry | null }) => {
  if (!entry) {
    return (
      <div className="commode-detail">
        <div className="detail-empty">
          Survole un objet pour voir ses détails.
        </div>
      </div>
    );
  }
  const def = resolveDef(entry);
  if (!def) {
    return (
      <div className="commode-detail">
        <div className="detail-empty">Objet inconnu.</div>
      </div>
    );
  }

  const trinketDef = def as TrinketMajorDefinition | TrinketMinorDefinition;
  const atCap = entry.level >= trinketDef.maxLevel;
  const cost = atCap
    ? null
    : computeTrinketLevelUpCost(trinketDef, entry.level);
  const affordable = cost ? canAfford(cost) : false;
  const gains = atCap ? [] : describeTrinketUpgrade(trinketDef, entry.level);

  const isMajor = entry.kind === 'major';
  const card = trinketCard(trinketDef.id, entry.level, isMajor);
  const related = card ? resolveCardClosure(card).slice(1) : [];

  return (
    <>
      {/* Floats LEFT of the detail frame — outside the scrolling panel so the
          overflow clip cannot swallow it. */}
      {related.length > 0 && (
        <div className="commode-related">
          {related.map((r) => (
            <ItemCard
              key={`${r.kind}:${r.id}`}
              icon={r.icon}
              name={r.name}
              stats={r.stats}
              lines={r.lines}
            />
          ))}
        </div>
      )}
      <div className="commode-detail">
        <div className="detail-name">{entry.name}</div>
        <div className="detail-meta">
          {KIND_LABEL[entry.kind]} · niveau {entry.level} /{' '}
          {trinketDef.maxLevel}
        </div>

        {card?.stats && (
          <div className="item-card-stats">
            {card.stats.map((s) => (
              <div className="item-card-stat" key={s.label}>
                <span className="item-card-stat-label">{s.label}</span>
                <span
                  className="item-card-stat-value"
                  style={s.color ? { color: s.color } : undefined}
                >
                  {s.value}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="detail-section-label">Effets actuels</div>
        <DescLines
          lines={card?.lines ?? describeTrinketCurrent(trinketDef, entry.level)}
        />

        {atCap ? (
          <div className="detail-cap">Niveau maximum atteint.</div>
        ) : (
          <>
            <div className="detail-section-label">
              Effet supplémentaire au niv {entry.level + 1}
            </div>
            {gains.length > 0 ? (
              <DescLines lines={gains} />
            ) : (
              <div className="detail-cap">Pas de nouvel effet à ce niveau.</div>
            )}
            {cost && <CostDisplay cost={cost} />}
            <div
              className={`detail-upgrade-hint${affordable ? '' : ' disabled'}`}
            >
              <KeyDisplay context="interface" action="CONFIRM" size="sm" />{' '}
              {affordable ? 'Améliorer' : 'Ressources insuffisantes'}
            </div>
          </>
        )}
      </div>
    </>
  );
};

const CostDisplay = ({ cost }: { cost: CraftCost }) => (
  <div className="cost-list">
    {cost.gold > 0 && (
      <div className="cost-item">
        <span className="cost-name">Or</span>
        <span className="cost-qty">{cost.gold}</span>
      </div>
    )}
    {Object.entries(cost.materials).map(([id, qty]) => (
      <div key={id} className="cost-item">
        <span className="cost-name">{ITEMS[id]?.label ?? id}</span>
        <span className="cost-qty">{qty}</span>
      </div>
    ))}
    {cost.gold === 0 && Object.keys(cost.materials).length === 0 && (
      <div className="cost-empty">Gratuit</div>
    )}
  </div>
);
